import { useSyncExternalStore } from "react";

/**
 * Tiny Shopify cart store — items persist in localStorage and checkout happens
 * via a Shopify cart permalink (no Storefront token needed).
 */

const KEY = "jm_shop_cart";
let listeners = [];

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
}

// Cached snapshot — useSyncExternalStore requires getSnapshot to return a
// stable reference between writes, otherwise it re-renders forever.
let current = load();

function write(items) {
  current = items;
  localStorage.setItem(KEY, JSON.stringify(items));
  listeners.forEach((l) => l());
}

export function addItem(product, variant, customization = null) {
  const items = [...current];
  const key = customization ? JSON.stringify(customization) : "";
  const existing = items.find((i) => i.variantId === variant.id && (i.customKey || "") === key);
  if (existing) existing.qty += 1;
  else {
    items.push({
      variantId: variant.id,
      title: product.title,
      variantTitle: variant.title !== "Default" ? variant.title : null,
      price: variant.price,
      image: product.image,
      qty: 1,
      customKey: key || undefined,
      customization: customization || undefined,
    });
  }
  write(items);
}

export function setQty(variantId, qty) {
  const items = current.map((i) => (i.variantId === variantId ? { ...i, qty } : i));
  write(items.filter((i) => i.qty > 0));
}

export function removeItem(variantId) {
  write(current.filter((i) => i.variantId !== variantId));
}

export function useCart() {
  return useSyncExternalStore(
    (cb) => {
      listeners.push(cb);
      return () => { listeners = listeners.filter((l) => l !== cb); };
    },
    () => current
  );
}

export function cartTotal(items) {
  return items.reduce((sum, i) => sum + i.price * i.qty, 0);
}

export function cartCount(items) {
  return items.reduce((sum, i) => sum + i.qty, 0);
}

export function checkoutUrl(items, checkoutBase) {
  const parts = items.map((i) => `${i.variantId}:${i.qty}`).join(",");
  let url = `${checkoutBase}${parts}`;
  // Customizations ride along as the cart note — visible on the order in Shopify.
  const customized = items.filter((i) => i.customization);
  if (customized.length) {
    const lines = customized.map((i) => {
      const c = i.customization;
      const bits = [`Design: ${c.design}`];
      if (c.text) bits.push(`Text: "${c.text}"`);
      if (c.artworkUrl) bits.push(`Artwork: ${c.artworkUrl}`);
      return `${i.title} (x${i.qty}) — ${bits.join(", ")}`;
    });
    url += `?note=${encodeURIComponent(`CUSTOMIZATION\n${lines.join("\n")}`)}`;
  }
  return url;
}
